import { useEffect, useRef, useState } from "react";
import { Ic, icons } from "../../components/Icons";
import useReveal from "../../components/useReveal";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useReducedMotion, useTransform } from "framer-motion";
import StudioDeviceRevealSection from "./StudioDeviceRevealSection";
import LiquidGlassButton from "../../components/LiquidGlassButton";
import ResponsiveHeroVideo from "../../components/ResponsiveHeroVideo";

const CHIP_SCRUB_DISTANCE = 1180;
const CHIP_LOCK_THRESHOLD = 10;

export default function HomePage() {
  useReveal();
  const [contactStatus, setContactStatus] = useState("idle");
  const [contactDraft, setContactDraft] = useState("");

  const submitContact = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.reportValidity()) return;
    try {
      const data = new FormData(form);
      const body = [
        `Name: ${data.get("name")}`,
        `Work email: ${data.get("email")}`,
        `Company: ${data.get("company") || "Not provided"}`,
        `Interest: ${data.get("interest")}`,
        "",
        data.get("message"),
      ].join("\r\n");
      const draft = `mailto:info@ateno.co?subject=${encodeURIComponent(`Website enquiry: ${data.get("interest")}`)}&body=${encodeURIComponent(body)}`;
      setContactDraft(draft);
      window.location.assign(draft);
      setContactStatus("ready");
    } catch {
      setContactStatus("error");
    }
  };
  const chipSectionRef = useRef(null);
  const chipProgressRef = useRef(0);
  const chipLockActiveRef = useRef(false);
  const chipTouchYRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const chipProgress = useMotionValue(0);
  const sectionFadeOpacity = useTransform(chipProgress, [0, 0.12], [0, 1]);
  const boardOpacity = useTransform(chipProgress, [0.5, 0.66, 0.92], [0, 0.34, 1]);
  const boardScale = useTransform(chipProgress, [0.5, 0.7, 0.96], [1.165, 1.165, 1]);
  const heroChipOpacity = useTransform(chipProgress, [0, 0.18, 0.78, 0.94], [0, 1, 1, 0]);
  const heroChipScale = useTransform(chipProgress, [0, 0.52, 0.96], [1, 1, 0.855]);
  const heroChipShadow = useTransform(chipProgress, [0.1, 0.56, 0.96], ["0 34px 76px rgba(0,0,0,.18)", "0 22px 70px rgba(14,165,233,.18)", "0 8px 24px rgba(0,0,0,.22)"]);
  const glareX = useTransform(chipProgress, [0.22, 0.46, 0.68], ["-94%", "6%", "94%"]);
  const glareOpacity = useTransform(chipProgress, [0.16, 0.32, 0.58, 0.74], [0, 1, 0.9, 0]);
  const glowOpacity = useTransform(chipProgress, [0.1, 0.32, 0.66, 0.9], [0, 1, 0.78, 0.1]);
  const glowScale = useTransform(chipProgress, [0.1, 0.5, 0.94], [0.9, 1.12, 0.88]);
  const chipMask = {
    WebkitMaskImage: 'url("/assets/home/ecosystem/ecosystem-ax.png")',
    maskImage: 'url("/assets/home/ecosystem/ecosystem-ax.png")',
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskPosition: "center",
    maskPosition: "center",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
  };

  useEffect(() => {
    if (reduceMotion) {
      chipProgress.set(1);
      chipProgressRef.current = 1;
      return undefined;
    }

    const clamp01 = (value) => Math.min(1, Math.max(0, value));

    const syncProgress = (next) => {
      const clamped = clamp01(next);
      chipProgressRef.current = clamped;
      chipProgress.set(clamped);
      return clamped;
    };

    const alignSection = () => {
      const section = chipSectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      if (Math.abs(rect.top) > 1) {
        window.scrollTo(window.scrollX, window.scrollY + rect.top);
      }
    };

    const shouldLock = (delta) => {
      const section = chipSectionRef.current;
      if (!section) return false;
      const rect = section.getBoundingClientRect();
      const progress = chipProgressRef.current;
      if (!chipLockActiveRef.current && ((delta > 0 && progress >= 1) || (delta < 0 && progress <= 0))) {
        return false;
      }
      const sectionAligned = Math.abs(rect.top) <= CHIP_LOCK_THRESHOLD && rect.bottom >= window.innerHeight - CHIP_LOCK_THRESHOLD;
      const enteringDown = delta > 0 && rect.top <= CHIP_LOCK_THRESHOLD && rect.bottom > window.innerHeight * 0.5 && progress < 1;
      const enteringUp = delta < 0 && rect.bottom >= window.innerHeight - CHIP_LOCK_THRESHOLD && rect.top < window.innerHeight * 0.5 && progress > 0;

      return chipLockActiveRef.current || sectionAligned || enteringDown || enteringUp;
    };

    const scrub = (delta) => {
      const current = chipProgressRef.current;
      const next = syncProgress(current + delta / CHIP_SCRUB_DISTANCE);

      if ((next >= 1 && delta > 0) || (next <= 0 && delta < 0)) {
        chipLockActiveRef.current = false;
      } else {
        chipLockActiveRef.current = true;
        alignSection();
      }
    };

    const handleWheel = (event) => {
      if (!shouldLock(event.deltaY)) return;

      event.preventDefault();
      chipLockActiveRef.current = true;
      alignSection();
      scrub(event.deltaY);
    };

    const handleTouchStart = (event) => {
      chipTouchYRef.current = event.touches?.[0]?.clientY ?? null;
    };

    const handleTouchMove = (event) => {
      if (chipTouchYRef.current == null) return;
      const currentY = event.touches?.[0]?.clientY ?? chipTouchYRef.current;
      const delta = chipTouchYRef.current - currentY;

      if (!shouldLock(delta)) {
        chipTouchYRef.current = currentY;
        return;
      }

      event.preventDefault();
      chipLockActiveRef.current = true;
      alignSection();
      scrub(delta * 1.5);
      chipTouchYRef.current = currentY;
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      chipLockActiveRef.current = false;
    };
  }, [chipProgress, reduceMotion]);
  const ecosystem = [
    {
      key: "studio",
      icon: icons.software,
      label: "Apps & Models",
      title: "Ateno Studio",
      titleColor: "var(--ecosystem-studio)",
      desc: "Design interiors from your phone, then upload objects and arrange them inside real 3D spaces.",
      audience: "Available now",
      picture: "/assets/home/ecosystem/ecosystem-studio.png",
      primaryLabel: "Get Now",
      primaryTo: "/software",
    },
    {
      key: "ax",
      icon: icons.chip,
      label: "Hardware",
      title: "Ateno AX",
      titleColor: "var(--ecosystem-ax)",
      desc: "Ateno AX is a coprocessor for 3D Gaussian Splatting workloads. Currently an FPGA prototype. Dedicated silicon is the next step.",
      audience: "AI / edge teams",
      picture: "/assets/home/ecosystem/ecosystem-ax.png",
      primaryLabel: "Explore More",
      primaryTo: "/ax",
    },
  ];

  return (
    <>
      <section className="hero home-hero home-hero-dark-nav" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "flex-start", padding: "126px 2rem 96px", textAlign: "left" }}>
        <div className="hero-video-layer" aria-hidden="true">
          <ResponsiveHeroVideo
            className="hero-video"
            desktopSrc="/assets/media/hero/hero-optimized.mp4"
            mobileSrc="/assets/media/hero/hero-mobile.mp4"
            poster="/assets/media/hero/hero-poster-optimized.jpg"
          />
        </div>
        <div className="hero-video-overlay hero-video-overlay-dark" />
        <div className="hero-video-overlay hero-video-overlay-directional" />
        <div className="hero-video-overlay hero-video-overlay-vignette" />
        <div className="home-hero-layout">
          <div className="home-hero-content">
            <h1 className="hero-headline">
              Hardware for 3D Gaussian Splatting
            </h1>
            <p className="hero-subhead">
              Ateno AX is a coprocessor for 3D Gaussian Splatting workloads. Currently an FPGA prototype. Dedicated silicon is the next step.
            </p>
            <div className="hero-cta-wrap" style={{ display: "flex", gap: 16, justifyContent: "flex-start", flexWrap: "wrap" }}>
              <LiquidGlassButton to="/ax" width={198} height={54} tone="dark" fallbackClassName="btn btn-white btn-lg btn-hero-primary">
                Explore Ateno AX <span aria-hidden="true">→</span>
              </LiquidGlassButton>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-darker ecosystem-section">
        <div className="container">
          <div className="ecosystem-header">
            <div className="reveal eyebrow">
              <Ic d={icons.layers} size={14} color="var(--accent-light)" />
              Ecosystem
            </div>
            <h2 className="reveal headline ecosystem-title">The complete spatial intelligence stack.</h2>
            <p className="reveal ecosystem-subhead">Apps for creation. Hardware for acceleration.<br />Everything designed to work together.</p>
          </div>

          <div className="ecosystem-grid">
            {ecosystem.map((product, i) => (
              <article key={product.key} className={`reveal reveal-d${i + 1} product-module product-module-${product.key}`}>
                <div
                  className={
                    [
                      "product-module-image",
                      `product-module-image-${product.key}`,
                      product.key === "studio" ? "product-module-image-studio" : "",
                    ].filter(Boolean).join(" ")
                  }
                  aria-hidden="true"
                >
                  {product.key === "studio" ? (
                    <img
                      src={product.picture}
                      alt={product.title}
                      className="product-module-image-inner product-module-phone-img product-module-phone-img-studio"
                      style={{
                        objectFit: "contain",
                        objectPosition: "center 82%",
                        width: "100%",
                        height: "100%",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        margin: 0,
                        display: "block",
                      }}
                      loading="lazy"
                    />
                  ) : product.picture ? (
                    <picture>
                      <img
                        src={product.picture}
                        alt={product.title}
                        className={[
                          "product-module-image-inner",
                          product.key === "studio" ? "product-module-phone-img" : "",
                          product.key === "ax" ? "product-module-chip-img" : "",
                          product.key === "sdk" ? "product-module-sdk-img" : ""
                        ].filter(Boolean).join(" ")}
                        style={{
                          objectFit: product.key === "studio" ? "cover" : "contain",
                          objectPosition: "center",
                          width: product.key === "studio" ? "100%" : "auto",
                          height: product.key === "studio" ? "100%" : "auto",
                          maxHeight: product.key === "studio" ? "none" : product.key === "ax" ? "160px" : product.key === "sdk" ? "200px" : undefined,
                          maxWidth: product.key === "studio" ? "none" : product.key === "ax" ? "160px" : product.key === "sdk" ? "200px" : undefined,
                          margin: "0 auto",
                          display: "block"
                        }}
                        loading="lazy"
                      />
                    </picture>
                  ) : (
                    <div className="product-module-image-fallback" />
                  )}
                </div>
                <div className="product-module-header">
                  <div className="product-module-icon" aria-hidden="true">
                    <Ic d={product.icon} size={16} color="var(--text-primary)" />
                  </div>
                  <div className="product-module-eyebrow">{product.label}</div>
                </div>
                <h3 className="product-module-title" style={{ color: product.titleColor }}>{product.title}</h3>
                <p className="product-module-desc">
                  {product.desc}
                  {product.note && <sup className="claim-note">{product.note}</sup>}
                </p>
                <div className="product-module-links" style={{ justifyContent: "center" }}>
                  <Link to={product.primaryTo} className="product-module-text-btn" style={{ color: product.titleColor }}><span className="product-module-text-btn-label">{product.primaryLabel}</span> <span className="product-module-text-btn-arrow">→</span></Link>
                  {product.secondaryTo && (product.secondaryTo.startsWith("http") ? (
                    <a href={product.secondaryTo} className="btn btn-md product-module-btn-ghost" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                      {product.secondaryLabel}
                    </a>
                  ) : (
                    <Link to={product.secondaryTo} className="btn btn-md product-module-btn-ghost">{product.secondaryLabel}</Link>
                  ))}
                </div>
              </article>
            ))}
          </div>

        </div>
      </section>

      <StudioDeviceRevealSection />


      <section ref={chipSectionRef} className="section home-chip-section" aria-labelledby="home-chip-title">
        <div className="container-wide home-chip-content">
          <div className="home-chip-visual" aria-hidden="true">
            <motion.div className="home-chip-stage" style={{ opacity: reduceMotion ? 1 : sectionFadeOpacity }}>
              <motion.img
                src="/assets/home/chip-on-board.png"
                alt=""
                className="home-chip-board-image"
                loading="lazy"
                decoding="async"
                style={{
                  opacity: reduceMotion ? 1 : boardOpacity,
                  scale: reduceMotion ? 1 : boardScale,
                }}
              />
              <motion.div
                className="home-chip-hero-chip"
                style={{
                  opacity: reduceMotion ? 0 : heroChipOpacity,
                  scale: reduceMotion ? 1 : heroChipScale,
                  boxShadow: reduceMotion ? "none" : heroChipShadow,
                }}
              >
                <motion.div className="home-chip-color-glow" style={{ opacity: reduceMotion ? 0 : glowOpacity, scale: reduceMotion ? 1 : glowScale }} />
                <img src="/assets/home/ecosystem/ecosystem-ax.png" alt="" className="home-chip-hero-image" loading="lazy" decoding="async" />
                <motion.div className="home-chip-edge-light" style={{ ...chipMask, opacity: reduceMotion ? 0 : glowOpacity }} />
                <motion.div className="home-chip-glare" style={{ ...chipMask, opacity: reduceMotion ? 0 : glareOpacity, x: reduceMotion ? 0 : glareX }} />
              </motion.div>
            </motion.div>
          </div>

          <div className="home-chip-copy">
            <div className="home-chip-eyebrow">
              <Ic d={icons.zap} size={14} />
              Ateno AX
            </div>
            <h2 id="home-chip-title" className="home-chip-title">A chip layer for spatial AI workloads.</h2>
            <p className="home-chip-subhead">
              Ateno AX is a coprocessor for 3D Gaussian Splatting workloads. Currently an FPGA prototype.
            </p>
            <div className="home-chip-actions">
              <LiquidGlassButton to="/ax" width={156} height={50} tone="dark" fallbackClassName="btn btn-white btn-lg btn-hero-primary">
                Explore AX <span aria-hidden="true">→</span>
              </LiquidGlassButton>
              <LiquidGlassButton to="/ax#architecture" width={170} height={46} tone="dark" fallbackClassName="btn btn-md btn-ghost home-chip-secondary">
                Product status
              </LiquidGlassButton>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="section home-final-cta-section" aria-labelledby="home-final-cta-title">
        <div className="container-wide home-final-cta-content">
          <div className="reveal reveal-d1 home-final-cta-eyebrow">
            <Ic d={icons.layers} size={14} />
            Contact
          </div>
          <h2 id="home-final-cta-title" className="reveal reveal-d2 home-final-cta-title">
            Talk to the team building the spatial stack.
          </h2>
          <p className="reveal reveal-d3 home-final-cta-subhead">
            Bring us your product, integration, or infrastructure question. We will route it to the right person on the Ateno team.
          </p>

          <form id="home-contact-form" className="reveal reveal-d4 home-contact-form" onSubmit={submitContact} onChange={() => setContactStatus("idle")}>
            <input type="hidden" name="source" value="home-spatial-stack" />
            <div className="home-contact-form-grid">
              <label className="home-contact-field">
                <span>Name</span>
                <input type="text" name="name" autoComplete="name" placeholder="Name" required />
              </label>
              <label className="home-contact-field">
                <span>Work email</span>
                <input type="email" name="email" autoComplete="email" placeholder="Work email" required />
              </label>
              <label className="home-contact-field">
                <span>Company</span>
                <input type="text" name="company" autoComplete="organization" placeholder="Company" />
              </label>
              <label className="home-contact-field">
                <span>Interest</span>
                <select name="interest" defaultValue="" required>
                  <option value="" disabled>Interest</option>
                  <option>Spatial stack</option>
                  <option>Ateno Studio</option>
                  <option>Ateno AX</option>
                  <option>Partnership</option>
                </select>
              </label>
            </div>
            <label className="home-contact-field home-contact-field-wide">
              <span>What are you building?</span>
              <textarea name="message" rows="5" placeholder="Tell us what you are building" required />
            </label>
            <p>This opens a draft to info@ateno.co in your email app. Send it there to contact the team.</p>
            {contactStatus === "ready" && (
              <div role="status">
                <p>Your email draft is ready. Send it from your email app to complete your request. Delivery has not been confirmed.</p>
                <a href={contactDraft}>Open the draft again</a>{" "}
                <button type="button" onClick={() => setContactStatus("error")}>Email app didn’t open?</button>
              </div>
            )}
            {contactStatus === "error" && (
              <p role="alert">We couldn’t confirm an email app opened. Your request has not been sent by this website. Email <a href="mailto:info@ateno.co">info@ateno.co</a> directly and copy the details above; your form is still here.</p>
            )}
            <div className="home-final-cta-actions">
              <LiquidGlassButton type="submit" form="home-contact-form" width={172} height={52} tone="light" fallbackClassName="home-contact-submit">
                <span>Open email draft</span>
                <span className="home-contact-submit-arrow" aria-hidden="true">→</span>
              </LiquidGlassButton>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
